/**
 * Open in Flow, the receiving side: Console Next (Mia-Platform Console v16) hands repositories to Flow as a manifest
 * plus one zip per repository, kept for 15 minutes at unguessable URLs (`/api/handoffs/<id>/…`, the 128-bit id is the
 * only access control), and opens Flow with `?consoleHandoff=<manifest URL>`. Flow, with the person's own session,
 * creates a sandbox project and imports each zip as `services/<name>`.
 *
 * Trust: the manifest URL comes from a query string anyone can craft, so it is accepted only from the configured
 * Console Next origin(s) (`consoleNextUrl`, `consoleNextWebUrl`) and only in the hand-off's own shape; every archive
 * must sit next to it (same origin, same hand-off id). Console Next's files are fetched without credentials: the
 * person's Flow token never leaves for Console Next.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import * as vfsClient from '../utils/vfsClient';
import { importArchiveIntoVfsFolder } from '../utils/gitTemplateClone';

export const HANDOFF_PARAM = 'consoleHandoff';
const PENDING_KEY = 'flow_console_handoff_pending';
const RECORD_PREFIX = 'flow_console_handoff::';
const MANIFEST_PATH = /^\/api\/handoffs\/([0-9a-f]{32})\/manifest\.json$/;
const ARCHIVE_PATH = /^\/api\/handoffs\/([0-9a-f]{32})\/[A-Za-z0-9.-]+\.zip$/;
const SERVICE_NAME = /^[a-z0-9][a-z0-9._-]{0,62}$/i;
// vfs-service's /upload takes at most 200 MB; Console Next keeps at most 150 MB of content per repository.
export const MAX_ARCHIVE_BYTES = 200 * 1024 * 1024;

export class HandoffError extends Error {
  constructor(message, kind = 'invalid') {
    super(message);
    this.name = 'HandoffError';
    this.kind = kind; // 'invalid' | 'expired' | 'unreachable'
  }
}

const originOf = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.origin : null;
  } catch {
    return null;
  }
};

/** The origins a hand-off may come from: Console Next's API and web app, as configured. */
export function allowedHandoffOrigins(cfg = config) {
  return [...new Set([cfg.consoleNextUrl, cfg.consoleNextWebUrl].map(originOf).filter(Boolean))];
}

/** Where to send the person back to Console Next (its web app, else its API origin), or null. */
export function consoleNextHome(cfg = config) {
  const base = cfg.consoleNextWebUrl || cfg.consoleNextUrl;
  return originOf(base) ? base.replace(/\/$/, '') : null;
}

/**
 * Checks a `consoleHandoff` value. Returns `{ manifestUrl, id, origin }`, or throws a HandoffError saying why it is
 * refused (not from the configured Console Next, or not a hand-off manifest).
 */
export function parseHandoffUrl(raw, origins = allowedHandoffOrigins()) {
  if (!origins.length) {
    throw new HandoffError('Flow is not connected to a Console Next (CONSOLE_NEXT_URL is not set), so it cannot take this hand-off.');
  }
  let url;
  try {
    url = new URL(String(raw || ''));
  } catch {
    throw new HandoffError('The link to open in Flow is not valid.');
  }
  if (!origins.includes(url.origin) || url.username || url.password) {
    throw new HandoffError(`This hand-off does not come from the Console Next Flow is connected to (${origins.join(', ')}), so Flow refused it.`);
  }
  const match = MANIFEST_PATH.exec(url.pathname);
  if (!match || url.search || url.hash) {
    throw new HandoffError('The link to open in Flow is not a Console Next hand-off.');
  }
  return { manifestUrl: `${url.origin}${url.pathname}`, id: match[1], origin: url.origin };
}

/** Console Next's files: no Flow credentials, no cookies. */
async function fetchFromConsole(url) {
  try {
    return await window.fetch(url, { credentials: 'omit', headers: { Accept: 'application/json, application/zip' } });
  } catch {
    throw new HandoffError(`Console Next is not reachable at ${new URL(url).origin}.`, 'unreachable');
  }
}

const unique = (name, taken) => {
  let candidate = name;
  for (let n = 2; taken.has(candidate.toLowerCase()); n += 1) { candidate = `${name}-${n}`; }
  taken.add(candidate.toLowerCase());
  return candidate;
};

/** The manifest's services, checked: each archive next to the manifest, a safe name, `services/<name>` as its folder. */
export function readManifest(body, { id, origin }) {
  if (!body || typeof body !== 'object' || !Array.isArray(body.services)) {
    throw new HandoffError('Console Next sent a hand-off Flow cannot read.');
  }
  if (body.id && body.id !== id) {
    throw new HandoffError('The hand-off does not match its link.');
  }
  const expiresAt = body.expiresAt ? Date.parse(body.expiresAt) : NaN;
  if (Number.isFinite(expiresAt) && expiresAt < Date.now()) {
    throw new HandoffError('This hand-off has expired.', 'expired');
  }
  const taken = new Set();
  const services = body.services.map((service) => {
    let archive;
    try { archive = new URL(service.archiveUrl); } catch { archive = null; }
    const archiveMatch = archive && ARCHIVE_PATH.exec(archive.pathname);
    if (!archive || archive.origin !== origin || !archiveMatch || archiveMatch[1] !== id || archive.search || archive.hash) {
      throw new HandoffError(`The archive of ${service.name || 'a service'} is not part of this hand-off.`);
    }
    const base = String(service.name || '').trim();
    if (!SERVICE_NAME.test(base) || base === '.' || base === '..') {
      throw new HandoffError(`"${base}" cannot be a service name.`);
    }
    const name = unique(base, taken);
    return {
      name,
      subfolder: `services/${name}`,
      repository: String(service.repository || base),
      archiveUrl: archive.href,
      files: Number.isFinite(service.files) ? service.files : null,
      bytes: Number.isFinite(service.bytes) ? service.bytes : null,
    };
  });
  if (!services.length) {
    throw new HandoffError('The hand-off has no repository to import.');
  }
  return {
    id,
    origin,
    name: String(body.name || 'console-project').slice(0, 60),
    from: String(body.from || ''),
    expiresAt: Number.isFinite(expiresAt) ? new Date(expiresAt).toISOString() : null,
    skipped: Array.isArray(body.skipped) ? body.skipped.map(String) : [],
    services,
  };
}

/** Fetches and checks the manifest. An expired (or unknown) hand-off rejects with kind `expired`. */
export async function fetchManifest(handoff) {
  const response = await fetchFromConsole(handoff.manifestUrl);
  if (response.status === 404 || response.status === 410) {
    throw new HandoffError('This hand-off has expired.', 'expired');
  }
  if (!response.ok) {
    throw new HandoffError(`Console Next answered ${response.status} for the hand-off.`, 'unreachable');
  }
  const body = await response.json().catch(() => null);
  return readManifest(body, handoff);
}

// ---- The hand-off in progress, per browser: which project it became and which services are in, so opening the same
// link again (a reload, a second click) resumes or just opens the project instead of making a second one.

export function rememberPending(raw) {
  try { sessionStorage.setItem(PENDING_KEY, raw); } catch { /* private mode */ }
}
export function pendingHandoff() {
  try { return sessionStorage.getItem(PENDING_KEY); } catch { return null; }
}
export function forgetPending() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* private mode */ }
}
export function readRecord(id) {
  try { return JSON.parse(localStorage.getItem(RECORD_PREFIX + id) || 'null'); } catch { return null; }
}
export function writeRecord(id, record) {
  try { localStorage.setItem(RECORD_PREFIX + id, JSON.stringify({ ...record, updatedAt: new Date().toISOString() })); } catch { /* full */ }
}

/** The service folders the project already has (a resumed hand-off skips them). */
export async function existingServices(projectId) {
  try {
    const { entries } = await vfsClient.listDirectoryEntries(projectId, 'services');
    return new Set(entries.filter((entry) => entry.type === 'directory').map((entry) => entry.name));
  } catch {
    return new Set();
  }
}

/** Downloads one archive in the browser (Console Next allows Flow's origin) and uploads it to vfs-service, extracted. */
async function uploadThroughBrowser(projectId, service, onPhase) {
  onPhase?.('downloading');
  const response = await fetchFromConsole(service.archiveUrl);
  if (response.status === 404 || response.status === 410) {
    throw new HandoffError('This hand-off has expired.', 'expired');
  }
  if (!response.ok) {
    throw new HandoffError(`Console Next answered ${response.status} for the archive.`, 'unreachable');
  }
  const blob = await response.blob();
  if (blob.size > MAX_ARCHIVE_BYTES) {
    throw new HandoffError(`The archive is ${Math.round(blob.size / 1048576)} MB, more than the ${MAX_ARCHIVE_BYTES / 1048576} MB Flow takes.`);
  }
  onPhase?.('uploading');
  const form = new FormData();
  form.append('path', service.subfolder);
  form.append('extract', 'true');
  form.append('archiveType', 'zip');
  form.append('file', blob, `${service.name}.zip`);
  const url = `${config.vfsServiceUrl.replace(/\/$/, '')}/api/v1/projects/${encodeURIComponent(projectId)}/upload`;
  const upload = await getAuthorizedFetch()(url, { method: 'POST', headers: { Accept: 'application/json' }, body: form });
  if (!upload.ok) {
    const body = await upload.json().catch(() => ({}));
    throw new Error(body.error || body.message || `Importing failed (${upload.status}).`);
  }
  vfsClient.markProjectVfsBacked(projectId);
  return upload.json().catch(() => ({}));
}

/**
 * Imports one service. vfs-service downloads the archive itself when it can reach Console Next (`import-archive`);
 * when it cannot (Console Next on the person's own machine, or behind a firewall), the browser carries it.
 * A folder that already exists is left as it is.
 */
export async function importService(projectId, service, { onPhase } = {}) {
  onPhase?.('importing');
  try {
    return await importArchiveIntoVfsFolder(projectId, service.subfolder, service.archiveUrl);
  } catch (error) {
    if (/\(409\)/.test(error?.message || '')) { return { subfolder: service.subfolder, existed: true }; }
    // Anything else (most often: vfs-service cannot reach Console Next): the browser downloads and uploads it.
  }
  return uploadThroughBrowser(projectId, service, onPhase);
}

/** "1.2 MB" */
export function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) { return null; }
  if (bytes < 1024) { return `${bytes} B`; }
  if (bytes < 1048576) { return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`; }
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/** The URL without `consoleHandoff` (path, other query values and hash kept). */
export function withoutHandoffParam(pathname, search, hash = '') {
  const params = new URLSearchParams(search);
  params.delete(HANDOFF_PARAM);
  const rest = params.toString();
  return `${pathname}${rest ? `?${rest}` : ''}${hash || ''}`;
}
